'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {  DropdownArrowIcon } from '@/components/icons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #101018;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 32px 6px 8px; /* add right padding for arrow icon */
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  min-height: 40px; /* reduce overall chip block height */
  transition: border-color 0.2s;
  cursor: pointer;
  position: relative;
  
  &:focus-within {
    outline: 2px solid #FB6F2A;
    outline-offset: 0;
    border-color: #FB6F2A;
  }
  
  &:hover:not(:focus-within) {
    border-color: #9ca3af;
  }
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px; /* thinner chip */
  background: #C4593D;
  color: #ffffff;
  font-size: 13px; /* slightly smaller */
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: 1px solid #ffffff;
    outline-offset: 1px;
  }
  
  svg {
    width: 10px;
    height: 10px;
    fill: #ffffff;
  }
`;

const DropdownContainer = styled.div<{ $isOpen: boolean }>`
  position: relative;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 9999;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

const DropdownItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 10px 16px;
  background: ${({ $selected }) => ($selected ? '#fef3f2' : 'transparent')};
  border: none;
  text-align: left;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &:focus {
    outline: 2px solid #FB6F2A;
    outline-offset: -2px;
  }
`;

const Checkmark = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 14px;
    height: 14px;
    stroke: #FB6F2A;
    strokeWidth: 2.5px;
  }
`;

const PlaceholderText = styled.span`
  color: #9ca3af;
  font-size: 14px;
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: #ef4444;
  margin-top: 4px;
`;

interface MultiSelectChipsProps {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder?: string;
  error?: string;
  helperIcon?: React.ReactNode;
  className?: string;
  /**
   * Optional customization hooks for advanced selection behaviors (e.g., grouped options).
   * If not provided, MultiSelectChips behaves like a simple multi-select.
   */
  isOptionSelected?: (option: string, value: string[]) => boolean;
  getToggledValue?: (option: string, value: string[]) => string[];
  getRemovedValue?: (option: string, value: string[]) => string[];
  getDisplayValues?: (value: string[]) => string[];
}

export const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select options',
  error,
  helperIcon,
  className,
  isOptionSelected,
  getToggledValue,
  getRemovedValue,
  getDisplayValues,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (option: string) => {
    if (getToggledValue) {
      onChange(getToggledValue(option, value));
      return;
    }
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const handleRemove = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (getRemovedValue) {
      onChange(getRemovedValue(option, value));
      return;
    }
    onChange(value.filter((v) => v !== option));
  };

  const displayValues = getDisplayValues ? getDisplayValues(value) : value;

  return (
    <Container className={className}>
      {label && (
        <Label>
          {label}
          {helperIcon}
        </Label>
      )}
      <DropdownContainer ref={containerRef} $isOpen={isOpen}>
        <ChipsContainer onClick={() => setIsOpen(!isOpen)}>
          {displayValues?.length === 0 ? (
            <PlaceholderText>{placeholder}</PlaceholderText>
          ) : (
            displayValues.map((item) => (
              <Chip key={item}>
                {item}
                <RemoveButton
                  type="button"
                  onClick={(e) => handleRemove(item, e)}
                  aria-label={`Remove ${item}`}
                >
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </RemoveButton>
              </Chip>
            ))
          )}
          <ArrowIcon $isOpen={isOpen}>
            <DropdownArrowIcon size={16} color="#6b7280" />
          </ArrowIcon>
        </ChipsContainer>
        <Dropdown $isOpen={isOpen}>
          {options.map((option) => {
            const isSelected = isOptionSelected ? isOptionSelected(option, value) : value?.includes(option);
            return (
              <DropdownItem
                key={option}
                type="button"
                $selected={isSelected}
                onClick={() => handleToggle(option)}
              >
                {option}
                {isSelected && (
                  <Checkmark>
                    <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
                      <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Checkmark>
                )}
              </DropdownItem>
            );
          })}
        </Dropdown>
      </DropdownContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};


